import { formatMessage as formatLegacyMessage } from '@umijs/max';
import React, { useState } from 'react';
import { history, useModel } from '@umijs/max';
import { usePermissionStore } from '@/stores/permissionStore';
import { authManager, AuthSystem } from '@/services/auth/authManager';
import { Button, Result, Space, Spin } from 'antd';
import { useShallow } from 'zustand/react/shallow';

interface LayoutPermissionGuardProps {
  children: React.ReactNode;
}

/**
 * Single startup gate for authenticated routes. Cached permissions can render
 * immediately; a cold load remains explicit and recoverable if it fails.
 */
const LayoutPermissionGuard: React.FC<LayoutPermissionGuardProps> = ({ children }) => {
  const { initialState } = useModel('@@initialState');
  const {
    isInitialized,
    isLoadingGroups,
    isLoadingUserPermissions,
    groupsError,
    userPermissionsError,
    userPermissions,
    initialize,
  } = usePermissionStore(
    useShallow((state) => ({
      isInitialized: state.isInitialized,
      isLoadingGroups: state.isLoadingGroups,
      isLoadingUserPermissions: state.isLoadingUserPermissions,
      groupsError: state.groupsError,
      userPermissionsError: state.userPermissionsError,
      userPermissions: state.userPermissions,
      initialize: state.initialize,
    })),
  );
  const [isRetrying, setIsRetrying] = useState(false);
  const hasUser = Boolean(initialState?.currentUser);
  const isReady =
    !hasUser ||
    Boolean(initialState?.permissionsLoaded) ||
    isInitialized ||
    userPermissions.length > 0;
  const isLoading = isLoadingGroups || isLoadingUserPermissions;
  const startupError = groupsError || userPermissionsError;

  const retry = async () => {
    setIsRetrying(true);
    try {
      await initialize();
    } finally {
      setIsRetrying(false);
    }
  };

  const signInAgain = () => {
    authManager.clearAuth(AuthSystem.ADMIN);
    usePermissionStore.getState().reset();
    history.replace('/user/login');
  };

  if (!isReady && startupError && !isLoading) {
    return (
      <Result
        status="warning"
        title={formatLegacyMessage({
          id: 'legacyUi.3f6b89d01ea2b1f4',
          defaultMessage: '無法載入工作權限',
        })}
        subTitle="請檢查網絡後重試；系統不會在權限狀態不明時顯示可能未授權的內容。"
        extra={
          <Space wrap>
            <Button type="primary" loading={isRetrying} onClick={() => void retry()}>
              {formatLegacyMessage({ id: 'legacyUi.70dd895b66db82bb', defaultMessage: '重新載入' })}
            </Button>
            <Button onClick={signInAgain}>
              {formatLegacyMessage({ id: 'legacyUi.ea9fa4ff9d9aa48a', defaultMessage: '重新登入' })}
            </Button>
          </Space>
        }
      />
    );
  }

  if (!isReady) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" />
        <span>
          {formatLegacyMessage({
            id: 'legacyUi.d282d37e149663fd',
            defaultMessage: '正在準備您的工作空間…',
          })}
        </span>
      </div>
    );
  }

  return <>{children}</>;
};

export default LayoutPermissionGuard;
